import { CalendarDays, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';

import { ProjectStatusBadge } from '../ProjectStatusBadge';
import { DocumentSection } from '../DocumentSection';
import { Breadcrumb } from '@/shared/components/Breadcrumb';
import { PageHeader } from '@/shared/components/PageHeader';
import { messages } from '@/shared/constants/messages';
import { formatDateTimeRange } from '@/shared/lib/dates';
import { formatList } from '@/shared/lib/format';
import type { AgendaProject } from '@/shared/types/domain';

interface AgendaOnlyProjectProps {
  project: AgendaProject;
  crumb: { label: string; to: string } | null;
}

/**
 * A **leitura reduzida** de um projeto fora do alcance da RLS
 * (spec-calendar.md §7.2, decisão 76).
 *
 * O caso: um líder vê no calendário a conferência aprovada de outro ministério e
 * clica nela. `projects_select` não devolve aquela linha, `fetchProject` volta
 * `null`, e a tela diria "projeto não encontrado" — que é **falso** e é a pior
 * frase possível ali, porque a pessoa acabou de vê-lo na tela anterior.
 *
 * **O que ela mostra é exatamente a projeção da RPC**: título, ministério,
 * coordenação, situação, janela e espaços. Nada aqui vai buscar mais nada.
 *
 * **O que ela NÃO mostra, e a ausência É a decisão:** descrição, orçamento,
 * público esperado, informações complementares, itens de apoio, linha do tempo,
 * contato do responsável, painel de decisão, alerta de conflito e botão de
 * editar. Nenhum desses componentes chega a ser montado — não é `display: none`,
 * é código que não roda.
 *
 * **Consequência de exposição, dita em voz alta:** qualquer pessoa pode digitar
 * `/projetos/<uuid>` e obter isto de qualquer projeto aprovado da própria
 * filial. É **exatamente** a exposição que o calendário já dá na tela ao lado —
 * não é vazamento novo, é a mesma decisão vista por outro endereço. O que
 * continua barrado pela RLS é tudo o mais.
 */
export function AgendaOnlyProject({ project, crumb }: AgendaOnlyProjectProps) {
  const title = project.title ?? 'Projeto sem título';

  return (
    <div>
      <PageHeader
        breadcrumb={
          <Breadcrumb
            items={[...(crumb ? [{ label: crumb.label, to: crumb.to }] : []), { label: title }]}
          />
        }
        title={title}
        description={
          <span className="flex flex-wrap items-center gap-x-1.5">
            {/* Os nomes vêm da RPC e são texto, não link: a tela do ministério
                é gateada pela RLS, e oferecer um caminho que devolve vazio é
                pior do que não oferecer caminho nenhum. */}
            <span className="font-medium text-foreground">{project.ministry_name}</span>
            <span aria-hidden="true" className="text-border">
              ·
            </span>
            <span>{project.coordination_name}</span>
          </span>
        }
        actions={<ProjectStatusBadge project={project} />}
      />

      <div className="min-w-0 max-w-3xl space-y-8">
        {/* A frase que explica o recorte, em linguagem de voluntário (RNF-U03).
            Ela vem ANTES dos fatos porque responde à pergunta que a pessoa traz
            ao chegar: "por que esta tela é menor do que a que eu conheço?" */}
        <p className="rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
          {messages.projects.agendaOnly}
        </p>

        <DocumentSection id="data-e-local" title={messages.projectSections.schedule}>
          <dl className="space-y-4">
            <Fact icon={<CalendarDays className="size-4" />} label={messages.projects.factDateTime}>
              {formatDateTimeRange(project.starts_at, project.ends_at)}
            </Fact>
            <Fact icon={<MapPin className="size-4" />} label={messages.projects.factPlace}>
              {project.space_names.length > 0 ? (
                formatList(project.space_names)
              ) : (
                <span className="font-normal text-muted-foreground">Nenhum espaço informado</span>
              )}
            </Fact>
          </dl>
        </DocumentSection>

        <Link
          to="/calendario"
          className="inline-block text-sm font-medium text-primary hover:underline"
        >
          Voltar para o calendário
        </Link>
      </div>
    </div>
  );
}

function Fact({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-w-0">
      <dt className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <span aria-hidden="true" className="shrink-0">
          {icon}
        </span>
        {label}
      </dt>
      <dd className="mt-1 text-sm font-medium">{children}</dd>
    </div>
  );
}
